// What one creative slot of a published campaign is doing, as pure functions
// over the plan, the sync status and the reporting rows.
//
// The creative step used to render a slot as its name and "Applied version N"
// (2026-09-18): nothing said whether the slot's ads were active, paused,
// being rolled out or failing to be added, and nothing said what the creative
// had delivered. Those answers exist — `platform_ads` (through the sync
// status), the three operation journals on the plan, and the reporting query
// — and this module is where they become one card's worth of words.

import type { Creative } from '@/api/types';
import type {
  CampaignSyncStage,
  CampaignSyncStatus,
  CampaignSyncTarget,
  ManageCampaignPlan,
} from '@/api/manage-types';
import type {
  CampaignCreativeRollout,
  CampaignCreativeSlotChange,
} from '@/api/types.gen';
import { parseAdStageKey, parseGroupStageKey } from '@/lib/campaign-publish-sequence';
import { countInFlightOperations } from '@/lib/campaign-sync-outcome';
import { libraryToAssets } from '@/lib/creative-library-assets';
import { fmtDate } from '@/lib/datetime';

// The count lives in `campaign-sync-outcome` (type-only imports) so the
// resource registry can poll on it without importing this module's asset
// helpers; the editor header reads it from here.
export { countInFlightOperations };

export type SlotTone = 'live' | 'paused' | 'busy' | 'failed' | 'quiet';

export interface SlotDelivery {
  tone: SlotTone;
  /** "Active in 2 ad sets", "Paused in 2 ad sets", "Rolling out version 2 · 1 of 2 ad sets" */
  headline: string;
  /** One line per audience: the provider object behind this slot there. */
  lines: { label: string; value: string }[];
  /** A sentence under the lines: since when, who paused it, what failed. */
  note?: string;
  /** An operation on this slot is in flight; the slot's own verbs wait. */
  busy: boolean;
}

type Slot = ManageCampaignPlan['creatives'][number];

const PROVIDER_NOUN = {
  meta: { one: 'ad set', many: 'ad sets', object: 'ad' },
  ttd: { one: 'ad group', many: 'ad groups', object: 'ad group' },
} as const;

const plural = (count: number, provider: CampaignSyncTarget) =>
  `${count} ${count === 1 ? PROVIDER_NOUN[provider].one : PROVIDER_NOUN[provider].many}`;

const isPausedStatus = (status: string | null | undefined) =>
  (status ?? '').toLowerCase().startsWith('paused');

const PROVIDER_NAME = { meta: 'Meta', ttd: 'The Trade Desk' } as const;

/** What a card says under a change in flight: it finishes without the page. */
export const LEAVE_PAGE_NOTE =
  'Usually under a minute. You can leave this page; the change finishes on its own and the card updates when you return.';

export type SlotStepState = 'done' | 'active' | 'failed' | 'pending';

export interface SlotChangeStep {
  key: string;
  /** "Request accepted", "Pausing the ad in Audience 2", "Confirmed by Meta's readback" */
  label: string;
  state: SlotStepState;
  /** The provider's sentence on a failed step, or "paused, then restored". */
  detail?: string;
  /** When the step landed, an instant. */
  at?: string;
}

export interface SlotChangeProgress {
  op: 'pause' | 'resume';
  provider: CampaignSyncTarget;
  status: CampaignCreativeSlotChange['status'];
  /** "Pausing on Meta", "Paused on Meta", "Pause did not complete" */
  headline: string;
  /** `requested_at`: when the request was accepted. */
  startedAt: string;
  steps: SlotChangeStep[];
  /** The change row's own sentence, on failure. */
  lastError?: string;
}

/**
 * Which audiences a failed change moved and then restored, and which it could
 * not restore, from the sentence the server records (`slot_change_partial_failure`
 * in `queue/campaign_sync.rs`, pinned there): "…the ad for audience 1, 2 had
 * already been paused and has been restored" / "…the ad for audience 3 is still
 * paused at the provider and could not be restored…". Zero-based segments.
 */
export function restoredSegmentsFromError(text: string | null | undefined): { restored: number[]; stuck: number[] } {
  const segments = (match: RegExpMatchArray | null) =>
    match?.[1]
      ? match[1].split(',').map((part) => Number(part.trim()) - 1).filter((n) => Number.isInteger(n) && n >= 0)
      : [];
  const source = text ?? '';
  return {
    restored: segments(/for audience ([\d, ]+) had already been (?:paused|resumed) and has been restored/.exec(source)),
    stuck: segments(/for audience ([\d, ]+) is still (?:paused|resumed) at the provider and could not be restored/.exec(source)),
  };
}

/**
 * The stepper a card shows while a slot change runs, and what each audience
 * came to when it failed. Two sources joined by the change id: the row on the
 * plan (accepted, confirmed, failed, its sentence) and the sync status's
 * journal rows under `slot_{id}_ad_{segment}` / `slot_{id}_group_{segment}`
 * (one per audience: running, complete, failed with the provider's words).
 */
export function slotChangeProgress(input: {
  change: CampaignCreativeSlotChange;
  stages: readonly CampaignSyncStage[];
  provider: CampaignSyncTarget;
  /** How many audiences the provider hierarchy has; the steps the change will take. */
  audiences: number;
}): SlotChangeProgress {
  const { change, stages, provider, audiences } = input;
  const pausing = change.op === 'pause';
  const verb = pausing ? 'Pausing' : 'Resuming';
  const moved = pausing ? 'paused' : 'resumed';
  const done = change.status === 'done';
  const failed = change.status === 'failed';
  const providerName = PROVIDER_NAME[provider];
  const prefix = `slot_${change.id}_`;
  const parse = provider === 'meta' ? parseAdStageKey : parseGroupStageKey;
  const bySegment = new Map<number, CampaignSyncStage>();
  for (const stage of stages) {
    if (!stage.stage_key.startsWith(prefix)) continue;
    const parsed = parse(stage.stage_key);
    if (parsed && parsed.lane === 'slot') bySegment.set(parsed.segment, stage);
  }
  const restore = restoredSegmentsFromError(failed ? change.last_error : null);
  let count = Math.max(0, audiences);
  for (const segment of bySegment.keys()) count = Math.max(count, segment + 1);
  const steps: SlotChangeStep[] = [
    { key: 'accepted', label: 'Request accepted', state: 'done', at: change.requested_at },
  ];
  for (let segment = 0; segment < count; segment += 1) {
    const n = segment + 1;
    const label = provider === 'meta'
      ? `${verb} the ad in Audience ${n}`
      : pausing
        ? `Detaching from ad group ${n}`
        : `Re-attaching to ad group ${n}`;
    const stage = bySegment.get(segment);
    let state: SlotStepState = done ? 'done' : 'pending';
    let detail: string | undefined;
    let at: string | undefined;
    if (stage?.status === 'complete') {
      state = 'done';
      at = stage.completed_at ?? stage.updated_at;
    } else if (stage?.status === 'failed') {
      state = 'failed';
      detail = stage.last_error ?? undefined;
      at = stage.updated_at;
    } else if (stage?.status === 'running') {
      state = 'active';
      at = stage.updated_at;
    }
    if (failed) {
      if (restore.restored.includes(segment)) {
        state = 'done';
        detail = `${moved}, then restored`;
      } else if (restore.stuck.includes(segment)) {
        state = 'failed';
        detail = `${moved}, could not be restored — the next reconciliation converges it on the plan`;
      } else if (state === 'active') {
        // The row failed while this step was running and nothing says it was
        // restored: the change's own sentence below is the account of it.
        state = 'failed';
      }
    }
    steps.push({ key: `audience-${segment}`, label, state, detail, at });
  }
  steps.push({
    key: 'readback',
    label: `Confirmed by ${providerName}'s readback`,
    state: done ? 'done' : failed ? 'failed' : 'pending',
    at: done ? (change.completed_at ?? undefined) : undefined,
  });
  return {
    op: change.op,
    provider,
    status: change.status,
    headline: done
      ? `${pausing ? 'Paused' : 'Resumed'} on ${providerName}`
      : failed
        ? `${pausing ? 'Pause' : 'Resume'} did not complete`
        : `${verb} on ${providerName}`,
    startedAt: change.requested_at,
    steps,
    lastError: failed ? (change.last_error ?? undefined) : undefined,
  };
}

/**
 * The change row a card should read for one slot, from three sources in
 * order of recency: the request the user just confirmed (a stub, until the
 * server answers), the row the mutation returned (until the plan carries a
 * row with that id), and the plan's newest row for the slot. Without the
 * first two the card sat unchanged for a whole re-read after the click.
 */
export function effectiveSlotChange(input: {
  planRow: CampaignCreativeSlotChange | null | undefined;
  mutationRow?: CampaignCreativeSlotChange | null;
  pending?: {
    position: number;
    op: 'pause' | 'resume';
    requestedAt: string;
    campaignId: string;
    campaignRevision: number;
    creativeId: string;
    target: CampaignSyncTarget;
  } | null;
}): CampaignCreativeSlotChange | null {
  const { planRow, mutationRow, pending } = input;
  if (pending) {
    return {
      id: `pending-${pending.position}`,
      campaign_id: pending.campaignId,
      campaign_revision: pending.campaignRevision,
      position: pending.position,
      creative_id: pending.creativeId,
      op: pending.op,
      target: pending.target,
      status: 'queued',
      requested_by: '',
      requested_by_name: null,
      requested_at: pending.requestedAt,
      started_at: null,
      completed_at: null,
      attempts: 0,
      last_error: null,
      details_json: '{}',
    };
  }
  if (mutationRow && planRow?.id !== mutationRow.id && (!planRow || planRow.requested_at <= mutationRow.requested_at)) {
    return mutationRow;
  }
  return planRow ?? null;
}

/**
 * Which rows changed status since the caller last looked — the completion
 * and failure toasts. `seen` is the caller's id → status map from the
 * previous read; rows it has never seen produce nothing (a change already in
 * flight when the page opened announces only its transition).
 */
export function slotChangeTransitions(
  seen: ReadonlyMap<string, string>,
  rows: readonly CampaignCreativeSlotChange[],
): { id: string; from: string; to: string; row: CampaignCreativeSlotChange }[] {
  const out: { id: string; from: string; to: string; row: CampaignCreativeSlotChange }[] = [];
  for (const row of rows) {
    const from = seen.get(row.id);
    if (from !== undefined && from !== row.status) out.push({ id: row.id, from, to: row.status, row });
  }
  return out;
}

/** The newest journal row for one slot, or null. Rows arrive newest first. */
export function latestForSlot<T extends { position: number }>(rows: T[] | undefined, position: number): T | null {
  return rows?.find((row) => row.position === position) ?? null;
}

export function slotDelivery(input: {
  provider: CampaignSyncTarget;
  slot: Slot;
  sync: CampaignSyncStatus | undefined;
  rollout?: CampaignCreativeRollout | null;
  change?: CampaignCreativeSlotChange | null;
}): SlotDelivery {
  const { provider, slot, sync, rollout, change } = input;
  const position = slot.position;
  const groups = (sync?.delivery_groups ?? []).filter((group) => group.target === provider);
  const groupCount = groups.length;
  const ads = (sync?.ads ?? [])
    .filter((ad) => ad.target === provider && ad.creative_position === position)
    .sort((a, b) => a.audience_segment_position - b.audience_segment_position);
  const lines = provider === 'meta'
    ? ads.map((ad) => ({
        label: `Audience ${ad.audience_segment_position + 1}`,
        value: ad.provider_ad_id ? `ad ${ad.provider_ad_id}` : (ad.last_error ? 'not created' : 'pending'),
      }))
    : groups.map((group) => ({
        label: `Audience ${group.audience_segment_position + 1}`,
        value: group.provider_delivery_group_id ? `ad group ${group.provider_delivery_group_id}` : 'pending',
      }));

  const inFlight = (status: string) => status === 'queued' || status === 'running';

  if (change && inFlight(change.status)) {
    const verb = change.op === 'pause' ? 'Pausing' : 'Resuming';
    return {
      tone: 'busy',
      busy: true,
      headline: `${verb} on ${PROVIDER_NAME[provider]}`,
      lines,
      // The change runs on the server and survives navigation; the card says
      // so instead of implying the user must wait here (2026-09-18).
      note: LEAVE_PAGE_NOTE,
    };
  }
  if (change && change.status === 'failed' && (change.op === 'pause') !== !!slot.paused_at) {
    const verb = change.op === 'pause' ? 'Pause' : 'Resume';
    return {
      tone: 'failed',
      busy: false,
      headline: `${verb} did not complete`,
      lines,
      // Only what the server recorded: after a partial failure its sentence
      // says which ad sets were restored, and nothing here may claim more.
      note: `${change.last_error ?? 'The provider did not confirm the change.'} Ask again to retry.`,
    };
  }
  if (rollout && inFlight(rollout.status)) {
    return {
      tone: 'busy',
      busy: true,
      headline: `Rolling out version ${rollout.to_revision}…`,
      lines,
      note: provider === 'meta'
        ? 'Meta is creating the new ad and pausing the old one; delivery resumes after review.'
        : 'The Trade Desk is swapping the creatives on the ad group; delivery does not stop.',
    };
  }
  if (rollout && rollout.status === 'failed' && slot.creative_revision === rollout.from_revision) {
    return {
      tone: 'failed',
      busy: false,
      headline: `Roll-out to version ${rollout.to_revision} did not complete`,
      lines,
      note: `${rollout.last_error ?? 'The provider did not confirm the new ad.'} Some steps may have completed; request the roll-out again to finish it.`,
    };
  }
  if (slot.paused_at) {
    return {
      tone: 'paused',
      busy: false,
      headline: provider === 'meta'
        ? `Paused in ${plural(Math.max(groupCount, ads.length), provider)}`
        : `Detached from ${plural(groupCount, provider)}`,
      lines,
      note: `Paused ${fmtDate(slot.paused_at)}. The other creatives keep delivering.`,
    };
  }
  if (!sync || (provider === 'meta' ? sync.ads === undefined : sync.delivery_groups === undefined)) {
    return { tone: 'quiet', busy: false, headline: 'Delivery status not read yet', lines: [] };
  }
  if (provider === 'ttd') {
    const live = groups.filter((group) => group.provider_delivery_group_id && !isPausedStatus(group.provider_status)).length;
    return {
      tone: live > 0 ? 'live' : 'quiet',
      busy: false,
      headline: live > 0 ? `Attached to ${plural(live, provider)}` : 'Not attached yet',
      lines,
    };
  }
  const created = ads.filter((ad) => ad.provider_ad_id);
  const pausedAtProvider = created.filter((ad) => isPausedStatus(ad.provider_status)).length;
  const active = created.length - pausedAtProvider;
  if (created.length === 0) {
    const failed = ads.find((ad) => ad.last_error);
    return {
      tone: failed ? 'failed' : 'quiet',
      busy: false,
      headline: failed ? 'Ad not created' : 'Not created yet',
      lines,
      note: failed?.last_error ?? undefined,
    };
  }
  if (pausedAtProvider > 0 && active === 0) {
    return {
      tone: 'quiet',
      busy: false,
      headline: `Paused at Meta in ${plural(created.length, provider)}`,
      lines,
      note: 'Held paused by the campaign or its flight, not by an operator.',
    };
  }
  return {
    tone: 'live',
    busy: false,
    headline: pausedAtProvider > 0
      ? `Active in ${active} of ${plural(created.length, provider)}`
      : `Active in ${plural(created.length, provider)}`,
    lines,
  };
}

export interface SlotMetrics {
  impressions: number;
  clicks: number;
  spendCents: number;
  videoStarts: number;
  videoCompletions: number;
  /** Distinct reported days behind the totals. */
  days: number;
  /** Impressions per reported day, oldest first, for a sparkline. */
  series: number[];
}

const n = (value: bigint | number | null | undefined): number =>
  value === null || value === undefined ? 0 : Number(value);

/**
 * The slice of a reporting row this module reads. Structural, so the rows
 * arrive as the wire shape (bigints) or as the app's `JsonNumbers` shape
 * alike.
 */
export interface MetricRow {
  dimensions: { creative_id?: string | null; day?: string | null };
  metrics: {
    impressions: bigint | number;
    clicks: bigint | number;
    billable_spend_cents: bigint | number;
    video_starts: bigint | number;
    video_completions: bigint | number;
  };
}

/**
 * Totals and a day series per creative id, from two reporting reads: one
 * grouped by creative, one by day and creative. Revisions are summed: the
 * card describes the creative the slot flies, whichever versions delivered.
 */
export function slotMetricsByCreative(
  totals: readonly MetricRow[] | undefined,
  daily: readonly MetricRow[] | undefined,
): Map<string, SlotMetrics> {
  const out = new Map<string, SlotMetrics>();
  const entry = (id: string) => {
    let current = out.get(id);
    if (!current) {
      current = { impressions: 0, clicks: 0, spendCents: 0, videoStarts: 0, videoCompletions: 0, days: 0, series: [] };
      out.set(id, current);
    }
    return current;
  };
  for (const row of totals ?? []) {
    const id = row.dimensions.creative_id;
    if (!id) continue;
    const metrics = entry(id);
    metrics.impressions += n(row.metrics.impressions);
    metrics.clicks += n(row.metrics.clicks);
    // Gross, the spend Reports shows everywhere: the media cost behind it is
    // not a figure an agency or a show reads.
    metrics.spendCents += n(row.metrics.billable_spend_cents);
    metrics.videoStarts += n(row.metrics.video_starts);
    metrics.videoCompletions += n(row.metrics.video_completions);
  }
  const byDay = new Map<string, Map<string, number>>();
  for (const row of daily ?? []) {
    const id = row.dimensions.creative_id;
    const day = row.dimensions.day;
    if (!id || !day) continue;
    let days = byDay.get(id);
    if (!days) {
      days = new Map();
      byDay.set(id, days);
    }
    days.set(day, (days.get(day) ?? 0) + n(row.metrics.impressions));
  }
  for (const [id, days] of byDay) {
    const metrics = entry(id);
    const ordered = [...days.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    metrics.days = ordered.length;
    metrics.series = ordered.map(([, impressions]) => impressions);
  }
  return out;
}

export interface CreativePoster {
  url: string;
  kind: 'image' | 'video';
  /** "4:5", "16:9", "300×250" */
  ratio: string;
}

/**
 * The one asset that stands for a creative on a card: the 4:5 feed image
 * for Meta (the card is portrait), the 16:9 video for CTV, the first banner
 * for Display. Null when no uploaded asset carries a read URL — the local
 * preview, where R2 secrets are unset, and a creative whose upload is not
 * finished both land here, and the card draws its initials poster instead.
 */
export function creativePoster(creative: Creative | undefined): CreativePoster | null {
  if (!creative) return null;
  const pick = (asset: { url?: string; mediaKind?: string } | null | undefined, ratio: string): CreativePoster | null =>
    asset?.url ? { url: asset.url, kind: asset.mediaKind === 'video' ? 'video' : 'image', ratio } : null;
  if (creative.channel === 'meta') {
    const assets = libraryToAssets(creative, null, 'meta');
    return pick(assets.fs, '4:5') ?? pick(assets.sq, '1:1') ?? pick(assets.story, '9:16');
  }
  if (creative.channel === 'ctv') return pick(libraryToAssets(creative, null, 'ctv').asset, '16:9');
  // Every other channel reads as Display, as `libraryToAssets` always has.
  const banner = libraryToAssets(creative, null, 'dsp').banners[0];
  return banner ? pick(banner, `${banner.w}×${banner.h}`) : null;
}

/** "SG" for "School Girls; · Conversion · Meta · Creative 1". */
export function posterInitials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').split(/\s+/).filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]!.toUpperCase()).join('');
  return initials || '·';
}

export interface SizeChip {
  key: string;
  label: string;
  present: boolean;
}

/** Which of Meta's three sizes a creative carries, in the order they run. */
export function metaSizeChips(creative: Creative | undefined): SizeChip[] {
  const tags = new Set((creative?.assets ?? []).flatMap((asset) => asset.placement_tags));
  return [
    { key: 'sq', label: '1:1', present: tags.has('meta_sq') },
    { key: 'fs', label: '4:5', present: tags.has('meta_fs') },
    { key: 'story', label: '9:16', present: tags.has('meta_story') },
  ];
}

/** "Image" / "Video" from the library row. */
export function mediaLabel(creative: Creative | undefined): string {
  return creative?.media_type === 'video' ? 'Video' : 'Image';
}
